/**
 * AlternateHrefLangs — Vilax shadow.
 *
 * Volto core (@plone/volto/components/theme/AlternateHrefLangs) has two
 * compounding bugs that crash public-view render for any non-Languageroot
 * content at site root:
 *
 *   (L-1) Guard uses `items && [...]` but `[]` is truthy in JavaScript,
 *         so empty arrays do not short-circuit the guard.
 *   (L-2) Inside the guarded block, `content.language.token` runs
 *         unsafely. For non-Languageroot content, Nick's document
 *         serializer omits the `language` key entirely (conditional
 *         spread, document.ts:466-472), so `content.language` is
 *         `undefined` and `.token` throws TypeError.
 *
 * Trigger example: creating a Folder at site root (e.g. `/media`) and
 * navigating to its public URL crashes the page with
 * "Cannot read properties of undefined (reading 'token')".
 *
 * Semantic rule this shadow enforces: the <link rel="alternate"
 * hrefLang="..."> tags are an SEO feature that exists to tell crawlers
 * which language variants of a page are available. Content that is not
 * inside any language context (no `language`, no `translation_group`)
 * has no variants and therefore needs no hreflang tags — the correct
 * output is `null`.
 *
 * Behaviour matrix (full matrix with both bugs fixed):
 *
 *   content.language  translations.items   output
 *   ----------------  -------------------  -----------------------------
 *   populated         non-empty            N translation links + self
 *   populated         empty / null / none  self link only
 *   missing           non-empty            N translation links, no self
 *   missing           empty / null / none  null (no tags)
 *
 * BUG-2026-094. Upstream PR to Volto core is a follow-up; this shadow
 * is the Vilax-side fix until the upstream change lands.
 */

import Helmet from '@plone/volto/helpers/Helmet/Helmet';
import { flattenToAppURL, toPublicURL } from '@plone/volto/helpers/Url/Url';

const AlternateHrefLangs = (props) => {
  const { content } = props;
  const currentLanguageToken = content?.language?.token;
  const translationItems =
    content?.['@components']?.translations?.items ?? [];

  const items = currentLanguageToken
    ? [
        ...translationItems,
        { '@id': content['@id'], language: currentLanguageToken },
      ]
    : translationItems;

  if (items.length === 0) {
    return null;
  }

  return (
    <Helmet>
      {items.map((item, key) => (
        <link
          key={key}
          rel="alternate"
          hrefLang={item.language}
          href={toPublicURL(flattenToAppURL(item['@id']))}
        />
      ))}
    </Helmet>
  );
};

export { AlternateHrefLangs };
